import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DeleteButton, Empty, ErrorBanner, Field, Loading, Modal } from '../../components/ui';
import { api } from '../../lib/api';
import { formatDate } from '../../lib/format';
import { useResource } from '../../lib/hooks';
import type { AmSop, AmSopMeta, ManualSection } from '../../lib/types';

const DEPARTMENTS = [
  'IT Operations',
  'Service Desk',
  'Security',
  'Sales',
  'Finance',
  'HR',
  'Management',
  'General',
];

/**
 * The SOP library.
 *
 * Deliberately separate from the operations manual. The manual is one authored
 * document with a fixed chapter structure; an SOP library is a flat set of
 * independently-versioned procedures, each with its own number, version,
 * effective date and approver. Folding one into the other would force one of
 * them to misrepresent what it is.
 *
 * An upload is a whole HTML document; the server lifts its body out and serves
 * it wrapped in a branded page with the document-control block on top. So the
 * thing that gets shared is a URL, not an attachment that immediately goes
 * stale in somebody's downloads folder.
 */
export function AmSops() {
  const resource = useResource<AmSop[]>('/am/sops');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<AmSop | 'new' | null>(null);
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  if (resource.loading) return <Loading label="Loading the SOP library" />;
  if (resource.error) return <ErrorBanner message={resource.error} />;
  const sops = resource.data ?? [];

  const q = search.trim().toLowerCase();
  const filtered = q
    ? sops.filter((s) =>
        [s.Title, s.FileName, s.Meta.department, s.Meta.sopNumber, s.SectionCode, s.SectionTitle]
          .filter(Boolean)
          .some((v) => (v as string).toLowerCase().includes(q)),
      )
    : sops;

  /**
   * Opens the rendered SOP in a new tab.
   *
   * Fetched rather than linked: the page sits behind the same bearer token as
   * the rest of the API, and a plain anchor in a new tab carries no
   * Authorization header — it would 401 every time.
   */
  async function open(sop: AmSop) {
    /* A linked procedure IS a manual section, so it opens in the manual — in
       this tab, as a normal navigation. Fetching and blob-opening it, the way
       an uploaded document has to be opened, would hand back a copy of the
       page and defeat the whole point of linking to it. */
    if (sop.SectionSlug) {
      navigate(`/operations-manual/${sop.SectionSlug}`);
      return;
    }
    try {
      const html = await api.getText(`/am/sops/${sop.AmSopId}/view`);
      const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
      const tab = window.open(url, '_blank', 'noopener');
      if (!tab) setError('The browser blocked the new tab. Allow pop-ups for this site.');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not open the SOP');
    }
  }

  async function remove(sop: AmSop) {
    try {
      await api.del(`/am/sops/${sop.AmSopId}`);
      resource.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete the SOP');
    }
  }

  return (
    <>
      <div className="row-between wrap mb-4">
        <div className="row">
          <input
            className="input"
            style={{ maxWidth: 280 }}
            placeholder="Search SOPs…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search SOPs"
          />
          <span className="xs muted">
            {filtered.length} of {sops.length}
          </span>
        </div>
        <div className="row">
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setLinking(true)}>
            Link to a manual section
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setEditing('new')}>
            Upload an SOP
          </button>
        </div>
      </div>

      {error && <ErrorBanner message={error} />}

      {sops.length === 0 ? (
        <Empty title="No procedures yet">
          <p>
            Upload an HTML document and it becomes a hosted, branded page with document control on
            top — SOP number, version, effective date, author and approver.
          </p>
          <p>
            Or link one: a procedure already written in the Operations Manual is listed here as
            itself, so there is never a second copy of it to keep in step.
          </p>
        </Empty>
      ) : (
        <div className="card">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Procedure</th>
                  <th style={{ width: 100 }}>SOP no.</th>
                  <th style={{ width: 80 }}>Version</th>
                  <th style={{ width: 150 }}>Department</th>
                  <th style={{ width: 120 }}>Effective</th>
                  <th style={{ width: 120 }}>Next review</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {filtered.map((sop) => (
                  <tr key={sop.AmSopId}>
                    <td>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => void open(sop)}>
                        {sop.Title}
                      </button>
                      <div className="xs muted">
                        {[
                          sop.SectionSlug
                            ? `Operations Manual${sop.SectionCode ? ` · ${sop.SectionCode}` : ''}`
                            : sop.FileName,
                          sop.Meta.author && `by ${sop.Meta.author}`,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                      {sop.SectionRetired && (
                        <div className="xs" style={{ color: 'var(--danger-600)' }}>
                          That section has been retired from the manual.
                        </div>
                      )}
                    </td>
                    <td className="xs mono">{sop.Meta.sopNumber || '—'}</td>
                    <td className="xs">{sop.Meta.version || '—'}</td>
                    <td className="xs">{sop.Meta.department || '—'}</td>
                    <td className="xs mono">
                      {formatDate(sop.Meta.effectiveDate ?? sop.CreatedUtc)}
                    </td>
                    <td className="xs mono">
                      {sop.Meta.reviewDate ? (
                        formatDate(sop.Meta.reviewDate)
                      ) : (
                        <span className="subtle">not set</span>
                      )}
                    </td>
                    <td className="actions">
                      {/* A linked row has no document of its own to re-upload
                          and no document-control block worth keeping here —
                          the manual holds its own version history. */}
                      {!sop.SectionSlug && (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => setEditing(sop)}
                        >
                          Edit
                        </button>
                      )}
                      <DeleteButton
                        what={
                          sop.SectionSlug
                            ? `"${sop.Title}" from this library (the manual section itself is untouched)`
                            : `"${sop.Title}" (the hosted page stops working)`
                        }
                        onConfirm={() => void remove(sop)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {linking && (
        <LinkSectionModal
          existing={sops}
          onClose={() => setLinking(false)}
          onSaved={() => {
            setLinking(false);
            resource.reload();
          }}
        />
      )}

      {editing && (
        <SopModal
          sop={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            resource.reload();
          }}
        />
      )}
    </>
  );
}

/**
 * Pick the manual section this procedure IS.
 *
 * A flat dropdown of every section, indented to show where each one sits,
 * rather than a chapter picker followed by a section picker. The manual is
 * ninety rows and people know the number they are after — "1C" — so one
 * searchable list gets them there in a keystroke, where two steps would make
 * them remember which chapter 1C was in.
 *
 * SECTIONS ALREADY LINKED ARE SHOWN AND DISABLED rather than left out. A
 * procedure missing from the list reads as a bug in the list; one greyed out
 * with "already linked" beside it answers the question being asked.
 *
 * The title is optional and prefilled from the section, which is the answer
 * almost every time. It is offered because the library's own naming is
 * sometimes the client-facing one — "Onboarding — client" where the manual
 * says "Track - Employee Onboarding".
 */
function LinkSectionModal({
  existing,
  onClose,
  onSaved,
}: {
  existing: AmSop[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const contents = useResource<ManualSection[]>('/manual');
  const [sectionId, setSectionId] = useState<number | ''>('');
  const [title, setTitle] = useState('');
  const [touchedTitle, setTouchedTitle] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sections = contents.data ?? [];
  const linked = new Set(existing.map((s) => s.ManualSectionId).filter((id): id is number => id !== null));

  /* Depth by walking up, the same bounded walk the manual page uses: the flat
     list is in contents order already, so indenting it is all that is needed
     to make the shape readable in a <select>. */
  const byId = new Map(sections.map((s) => [s.SectionId, s]));
  const depthOf = (section: ManualSection) => {
    let depth = 1;
    let cursor = section.ParentSectionId;
    let guard = 0;
    while (cursor !== null && guard++ < 6) {
      depth += 1;
      cursor = byId.get(cursor)?.ParentSectionId ?? null;
    }
    return depth;
  };

  const chosen = sectionId === '' ? null : (byId.get(sectionId) ?? null);

  async function save() {
    if (sectionId === '') return;
    setBusy(true);
    setError(null);
    try {
      await api.post('/am/sops/link', {
        manualSectionId: sectionId,
        ...(title.trim() ? { title: title.trim() } : {}),
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not link that section');
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Link to a manual section"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || sectionId === ''}
            onClick={() => void save()}
          >
            {busy ? 'Linking…' : 'Link it'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}
      {contents.loading ? (
        <Loading label="Loading the manual" />
      ) : (
        <>
          <Field
            label="Section"
            hint="The procedure as it is written in the Operations Manual. The library links to it rather than keeping a copy."
          >
            <select
              className="select"
              value={sectionId}
              onChange={(e) => {
                const next = e.target.value === '' ? '' : Number(e.target.value);
                setSectionId(next);
                if (!touchedTitle) {
                  const section = next === '' ? null : (byId.get(next) ?? null);
                  setTitle(section?.Title ?? '');
                }
              }}
            >
              <option value="">Choose a section…</option>
              {sections.map((section) => (
                <option
                  key={section.SectionId}
                  value={section.SectionId}
                  disabled={linked.has(section.SectionId)}
                >
                  {' '.repeat((depthOf(section) - 1) * 4)}
                  {section.Code ? `${section.Code} — ` : ''}
                  {section.Title}
                  {linked.has(section.SectionId) ? ' (already linked)' : ''}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Listed as" hint="Defaults to the section's own title.">
            <input
              className="input"
              value={title}
              onChange={(e) => {
                setTouchedTitle(true);
                setTitle(e.target.value);
              }}
              placeholder={chosen?.Title ?? 'Pick a section first'}
            />
          </Field>
        </>
      )}
    </Modal>
  );
}

const BLANK_META = (): AmSopMeta => ({
  sopNumber: '',
  version: '1.0',
  effectiveDate: new Date().toISOString().slice(0, 10),
  reviewDate: '',
  department: '',
  author: '',
  approver: '',
  purpose: '',
  scope: '',
  responsibilities: '',
});

/** Pull a title out of the uploaded document, falling back to the file name. */
function htmlTitle(html: string, fileName: string): string {
  const match = /<title[^>]*>([^<]+)<\/title>/i.exec(html);
  return match?.[1]?.trim() || fileName.replace(/\.html?$/i, '');
}

function SopModal({
  sop,
  onClose,
  onSaved,
}: {
  sop: AmSop | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(sop?.Title ?? '');
  const [meta, setMeta] = useState<AmSopMeta>({ ...BLANK_META(), ...(sop?.Meta ?? {}) });
  const [file, setFile] = useState<{ name: string; html: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const set = (key: keyof AmSopMeta) => (value: string) => setMeta((m) => ({ ...m, [key]: value }));

  async function onFile(input: HTMLInputElement) {
    const chosen = input.files?.[0];
    input.value = '';
    if (!chosen) return;
    try {
      const html = await chosen.text();
      if (!html.trim()) throw new Error('the file is empty');
      setFile({ name: chosen.name, html });
      if (!title.trim()) setTitle(htmlTitle(html, chosen.name));
      setError(null);
    } catch (err) {
      setError(`Could not read ${chosen.name}: ${err instanceof Error ? err.message : 'unknown error'}`);
    }
  }

  async function submit() {
    if (!sop && !file) {
      setError('Upload the procedure document before saving.');
      return;
    }
    setSaving(true);
    setError(null);
    const body: Record<string, unknown> = { title: title.trim(), meta };
    if (file) {
      body.html = file.html;
      body.fileName = file.name;
    }
    try {
      if (sop) await api.put(`/am/sops/${sop.AmSopId}`, body);
      else await api.post('/am/sops', body);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the SOP');
      setSaving(false);
    }
  }

  return (
    <Modal
      title={sop ? 'Edit SOP' : 'New SOP'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !title.trim()}
            onClick={() => void submit()}
          >
            {saving ? 'Saving…' : sop ? 'Save changes' : 'Create the page'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}
      <div className="form-grid">
        <Field label="Name of the procedure">
          <input
            className="input"
            placeholder="e.g. New client onboarding"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </Field>
        <Field label="SOP number">
          <input
            className="input"
            placeholder="e.g. SOP-001"
            value={meta.sopNumber ?? ''}
            onChange={(e) => set('sopNumber')(e.target.value)}
          />
        </Field>
        <Field label="Version">
          <input
            className="input"
            placeholder="1.0"
            value={meta.version ?? ''}
            onChange={(e) => set('version')(e.target.value)}
          />
        </Field>
        <Field label="Effective date">
          <input
            className="input"
            type="date"
            value={meta.effectiveDate ?? ''}
            onChange={(e) => set('effectiveDate')(e.target.value)}
          />
        </Field>
        <Field label="Next review date" hint="A procedure nobody re-reads is a procedure nobody follows.">
          <input
            className="input"
            type="date"
            value={meta.reviewDate ?? ''}
            onChange={(e) => set('reviewDate')(e.target.value)}
          />
        </Field>
        <Field label="Department">
          <input
            className="input"
            list="am-sop-departments"
            value={meta.department ?? ''}
            onChange={(e) => set('department')(e.target.value)}
          />
          <datalist id="am-sop-departments">
            {DEPARTMENTS.map((d) => (
              <option key={d} value={d} />
            ))}
          </datalist>
        </Field>
        <Field label="Written by">
          <input
            className="input"
            value={meta.author ?? ''}
            onChange={(e) => set('author')(e.target.value)}
          />
        </Field>
        <Field label="Approved by">
          <input
            className="input"
            value={meta.approver ?? ''}
            onChange={(e) => set('approver')(e.target.value)}
          />
        </Field>
      </div>

      <Field label="Purpose">
        <textarea
          className="textarea"
          rows={2}
          placeholder="Why this procedure exists — what outcome it guarantees"
          value={meta.purpose ?? ''}
          onChange={(e) => set('purpose')(e.target.value)}
        />
      </Field>
      <Field label="Scope">
        <textarea
          className="textarea"
          rows={2}
          placeholder="What and who it covers, and what it does not"
          value={meta.scope ?? ''}
          onChange={(e) => set('scope')(e.target.value)}
        />
      </Field>
      <Field label="Responsibilities">
        <textarea
          className="textarea"
          rows={2}
          placeholder="The roles involved and what each is accountable for"
          value={meta.responsibilities ?? ''}
          onChange={(e) => set('responsibilities')(e.target.value)}
        />
      </Field>

      <Field
        label="Procedure document"
        hint="An HTML file. Its body becomes the Procedure section; its own styles are kept, its scripts are not."
      >
        <input
          ref={fileRef}
          type="file"
          accept=".html,.htm"
          style={{ display: 'none' }}
          onChange={(e) => void onFile(e.currentTarget)}
        />
        <div className="row">
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => fileRef.current?.click()}>
            Choose a file
          </button>
          <span className={file || sop ? 'xs' : 'xs subtle'}>
            {file
              ? file.name
              : sop
                ? `${sop.FileName ?? 'current document'} — keeping the current file`
                : 'No file chosen'}
          </span>
        </div>
      </Field>
    </Modal>
  );
}
